import React, { useState, useRef, useCallback, useEffect } from 'react';  
import { motion, AnimatePresence } from 'framer-motion';  
import { ChevronLeft, ChevronRight, Star, Heart, Play, Clock, Calendar, RotateCcw, X, Info } from 'lucide-react';  

// Mock function - replace with your actual import
const getWatchProgress = (id) => 0;

// Modal de Detalles
const MovieDetailModal = ({ movie, isOpen, onClose, onPlayVideo, onToggleFavorite, isFavorite, progress }) => {
  const hasProgress = progress > 0;

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm"
        onClick={onClose}
      >
        <motion.div
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.9, opacity: 0 }}
          transition={{ type: "spring", duration: 0.5 }}
          className="relative w-full max-w-4xl max-h-[90vh] overflow-y-auto bg-gradient-to-b from-gray-900 to-black rounded-2xl shadow-2xl"
          onClick={(e) => e.stopPropagation()}
        >
          <button
            onClick={onClose}
            className="absolute top-4 right-4 z-10 p-2 bg-black/50 hover:bg-black/70 rounded-full backdrop-blur-sm border border-white/20 text-white transition-all"
          >
            <X className="w-6 h-6" />
          </button>

          <div className="relative h-96">
            <img
              src={movie.image}
              alt={movie.title}
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-gray-900 via-gray-900/50 to-transparent" />
          </div>

          <div className="p-8 -mt-32 relative z-10">
            <h2 className="text-4xl md:text-5xl font-black text-white mb-4">
              {movie.title}
            </h2>

            {hasProgress && (
              <div className="mb-6">
                <div className="flex items-center gap-3 mb-2">
                  <RotateCcw className="w-4 h-4 text-green-400" />
                  <span className="text-green-400 font-semibold text-sm">Continuar viendo</span>
                  <span className="text-gray-400 text-sm">{progress}% completado</span>
                </div>
                <div className="w-full bg-gray-700/50 rounded-full h-2">
                  <div 
                    className="bg-green-500 h-2 rounded-full transition-all duration-500"
                    style={{ width: `${progress}%` }}
                  />
                </div>
              </div>
            )}

            <div className="flex flex-wrap items-center gap-4 mb-6">
              <div className="flex items-center gap-2 bg-yellow-500/20 px-3 py-1 rounded-full">
                <Star className="w-5 h-5 fill-yellow-400 text-yellow-400" />
                <span className="font-bold text-yellow-400">{movie.rating}/10</span>
              </div>
              {movie.year && (
                <div className="flex items-center gap-2 bg-blue-500/20 px-3 py-1 rounded-full">
                  <Calendar className="w-4 h-4 text-blue-400" />
                  <span className="font-medium text-blue-400">{movie.year}</span>
                </div>
              )}
              {movie.duration && (
                <div className="flex items-center gap-2 bg-green-500/20 px-3 py-1 rounded-full">
                  <Clock className="w-4 h-4 text-green-400" />
                  <span className="font-medium text-green-400">{movie.duration}</span>
                </div>
              )}
            </div>

            {movie.genres && movie.genres.length > 0 && (
              <div className="flex flex-wrap gap-2 mb-6">
                {movie.genres.map((genre, index) => (
                  <span 
                    key={index}
                    className="px-3 py-1 bg-white/10 rounded-full text-sm text-gray-200 border border-white/10"
                  >
                    {genre}
                  </span>
                ))}
              </div>
            )}

            <div className="mb-8">
              <h3 className="text-xl font-bold text-white mb-3">Sinopsis</h3>
              <p className="text-gray-300 text-lg leading-relaxed">
                {movie.description || 'Una historia épica que no querrás perderte.'}
              </p>
            </div>

            <div className="flex flex-col sm:flex-row gap-4">
              <button
                onClick={() => {
                  onPlayVideo(movie, progress);
                  onClose();
                }}
                className="bg-white text-black px-8 py-4 rounded-xl font-bold text-lg flex items-center justify-center gap-3 hover:bg-gray-100 transition-all flex-1"
              >
                <Play className="w-6 h-6" />
                {hasProgress ? 'Continuar' : 'Reproducir Ahora'}
              </button>

              <button
                onClick={() => onToggleFavorite(movie.id)}
                className={`px-8 py-4 rounded-xl font-bold text-lg flex items-center justify-center gap-3 transition-all flex-1 ${
                  isFavorite 
                    ? 'bg-red-500/20 text-red-400 border-2 border-red-500/50' 
                    : 'bg-gray-600/80 text-white border-2 border-gray-500/50'
                }`}
              >
                <Heart className={`w-6 h-6 ${isFavorite ? 'fill-current' : ''}`} />
                {isFavorite ? 'En Mi Lista' : 'Agregar a Mi Lista'}
              </button>
            </div>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

const MovieCarousel = ({ 
  title, 
  movies = [], 
  onToggleFavorite = () => {}, 
  favorites = [], 
  onPlayVideo = () => {},
  watchProgress = {}
}) => {  
  const [scrollX, setScrollX] = useState(0);  
  const [hoveredMovie, setHoveredMovie] = useState(null);
  const [selectedMovie, setSelectedMovie] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [startX, setStartX] = useState(0);
  const [dragX, setDragX] = useState(0);

  const carouselRef = useRef(null);
  const containerRef = useRef(null);

  const getContainerWidth = () => {
    return containerRef.current ? containerRef.current.offsetWidth - 64 : window.innerWidth - 64;
  };

  const getContentWidth = () => {
    return movies.length * 280;
  };

  const getMaxScroll = useCallback(() => {
    return Math.max(0, getContentWidth() - getContainerWidth());
  }, [movies.length]);

  const animateScroll = useCallback((targetScroll) => {
    const startScroll = scrollX;
    const distance = targetScroll - startScroll;
    const duration = 400;
    const startTime = performance.now();

    const animate = (currentTime) => {
      const elapsed = currentTime - startTime;
      const progress = Math.min(elapsed / duration, 1);
      const easeProgress = 1 - Math.pow(1 - progress, 3);
      
      setScrollX(startScroll + distance * easeProgress);

      if (progress < 1) {
        requestAnimationFrame(animate);
      }
    };

    requestAnimationFrame(animate);
  }, [scrollX]);

  const scrollLeft = useCallback(() => {  
    const newScroll = Math.max(scrollX - 400, 0);
    animateScroll(newScroll);
  }, [scrollX, animateScroll]);  

  const scrollRight = useCallback(() => {  
    const newScroll = Math.min(scrollX + 400, getMaxScroll());
    animateScroll(newScroll);
  }, [scrollX, animateScroll, getMaxScroll]);

  const handleMouseDown = (e) => {
    setIsDragging(true);
    setStartX(e.clientX - carouselRef.current.offsetLeft);
    setDragX(scrollX);
    e.preventDefault();
  };

  const handleTouchStart = (e) => {
    setIsDragging(true);
    setStartX(e.touches[0].clientX - carouselRef.current.offsetLeft);
    setDragX(scrollX);
  };

  const handleMouseMove = useCallback((e) => {
    if (!isDragging) return;
    
    const x = e.clientX - carouselRef.current.offsetLeft;
    const walk = (x - startX) * 1.5;
    
    const newScrollX = Math.max(0, Math.min(getMaxScroll(), dragX - walk));
    setScrollX(newScrollX);
  }, [isDragging, startX, dragX, getMaxScroll]);

  const handleTouchMove = useCallback((e) => {
    if (!isDragging) return;
    
    const x = e.touches[0].clientX - carouselRef.current.offsetLeft;
    const walk = (x - startX) * 1.5;
    
    const newScrollX = Math.max(0, Math.min(getMaxScroll(), dragX - walk));
    setScrollX(newScrollX);
  }, [isDragging, startX, dragX, getMaxScroll]);

  const handleDragEnd = () => {
    setIsDragging(false);
  };

  // Wheel handler con useCallback para dependencias estables
  const handleWheel = useCallback((e) => {
    // Solo interceptar si hay scroll horizontal explícito
    if (Math.abs(e.deltaX) > Math.abs(e.deltaY)) {
      // El usuario está haciendo scroll horizontal (trackpad lateral)
      e.preventDefault();
      const newScrollX = Math.max(0, Math.min(getMaxScroll(), scrollX + e.deltaX));
      setScrollX(newScrollX);
    } else if (e.shiftKey && e.deltaY !== 0) {
      // Shift + scroll vertical = scroll horizontal en el carrusel
      e.preventDefault();
      const scrollAmount = e.deltaY * 0.5;
      const newScrollX = Math.max(0, Math.min(getMaxScroll(), scrollX + scrollAmount));
      setScrollX(newScrollX);
    }
    // Si no cumple ninguna condición, dejar que el scroll vertical normal funcione
  }, [scrollX, getMaxScroll]);

  // Event listeners para drag
  useEffect(() => {
    const handleGlobalMouseMove = (e) => {
      if (isDragging) {
        handleMouseMove(e);
      }
    };

    const handleGlobalMouseUp = () => {
      if (isDragging) {
        handleDragEnd();
      }
    };

    if (isDragging) {
      document.addEventListener('mousemove', handleGlobalMouseMove);
      document.addEventListener('mouseup', handleGlobalMouseUp);
      document.addEventListener('touchmove', handleTouchMove, { passive: false });
      document.addEventListener('touchend', handleDragEnd);
    }

    return () => {
      document.removeEventListener('mousemove', handleGlobalMouseMove);
      document.removeEventListener('mouseup', handleGlobalMouseUp);
      document.removeEventListener('touchmove', handleTouchMove);
      document.removeEventListener('touchend', handleDragEnd);
    };
  }, [isDragging, handleMouseMove, handleTouchMove]);

  // Event listener para wheel con passive: false
  useEffect(() => {
    const carousel = carouselRef.current;
    if (!carousel) return;

    carousel.addEventListener('wheel', handleWheel, { passive: false });

    return () => {
      carousel.removeEventListener('wheel', handleWheel);
    };
  }, [handleWheel]);

  const openModal = (movie) => {
    setSelectedMovie(movie);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setTimeout(() => setSelectedMovie(null), 300);
  };

  if (!movies || movies.length === 0) return null;

  return (  
    <motion.section  
      initial={{ opacity: 0, y: 50 }}  
      whileInView={{ opacity: 1, y: 0 }}  
      transition={{ duration: 0.6 }}  
      className="py-12 md:py-16 relative"  
    >  
      <div className="container mx-auto px-4" ref={containerRef}>  
        <div className="flex items-center justify-between mb-8 md:mb-12">  
          <div>
            <h3 className="text-2xl md:text-3xl lg:text-4xl font-bold text-white drop-shadow-lg mb-2">{title}</h3>
            <p className="text-gray-400 text-sm md:text-base">{movies.length} películas</p>
          </div>  
          <div className="flex gap-2">  
            <motion.button  
              onClick={scrollLeft}  
              className="bg-gray-800/50 hover:bg-gray-700/50 text-gray-400 hover:text-white p-2.5 rounded-lg transition-all duration-300 backdrop-blur-sm border border-gray-700/50 hover:border-gray-600/50"  
              whileHover={{ scale: 1.1, x: -2 }}  
              whileTap={{ scale: 0.9 }}  
              disabled={scrollX === 0}  
            >  
              <ChevronLeft className="w-4 h-4 md:w-5 md:h-5" />  
            </motion.button>  
            <motion.button  
              onClick={scrollRight}  
              className="bg-gray-800/50 hover:bg-gray-700/50 text-gray-400 hover:text-white p-2.5 rounded-lg transition-all duration-300 backdrop-blur-sm border border-gray-700/50 hover:border-gray-600/50"  
              whileHover={{ scale: 1.1, x: 2 }}  
              whileTap={{ scale: 0.9 }}  
              disabled={scrollX >= getMaxScroll()}  
            >  
              <ChevronRight className="w-4 h-4 md:w-5 md:h-5" />  
            </motion.button>  
          </div>  
        </div>  

        <div className="relative">  
          {scrollX > 0 && (  
            <motion.button  
              onClick={scrollLeft}  
              className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-2 z-20 bg-black/80 hover:bg-black text-white p-3 rounded-full backdrop-blur-sm border border-gray-700/50 shadow-2xl hidden md:block"  
              whileHover={{ scale: 1.1, x: -2 }}  
              whileTap={{ scale: 0.9 }}  
              initial={{ opacity: 0, x: -10 }}  
              animate={{ opacity: 1, x: 0 }}  
            >  
              <ChevronLeft className="w-5 h-5" />  
            </motion.button>  
          )}  

          <div 
            className="overflow-hidden cursor-grab active:cursor-grabbing"
            ref={carouselRef}
            onMouseDown={handleMouseDown}
            onTouchStart={handleTouchStart}
            style={{ userSelect: isDragging ? 'none' : 'auto' }}
          >  
            <div  
              className="flex gap-4 md:gap-6 pb-4"  
              style={{ 
                transform: `translateX(-${scrollX}px)`,
                transition: isDragging ? 'none' : 'transform 0.3s ease-out'
              }}
            >  
              {movies.map((movie, index) => {
                const progress = watchProgress[movie.id] || getWatchProgress(movie.id) || 0;
                const hasProgress = progress > 0;
                const isFavorite = favorites.includes(movie.id);
                
                return (
                <div  
                  key={movie.id}  
                  className="flex-shrink-0 w-48 md:w-64 lg:w-72 select-none"
                  onMouseEnter={() => setHoveredMovie(movie.id)}  
                  onMouseLeave={() => setHoveredMovie(null)}
                >  
                  <div className="group relative bg-gray-800/30 backdrop-blur-sm rounded-xl md:rounded-2xl overflow-hidden border border-gray-700/50 hover:border-gray-600/70 transition-all duration-500 hover:shadow-2xl hover:shadow-red-500/10 cursor-pointer h-full">  
                    
                    <div className="relative h-60 md:h-80 lg:h-96 overflow-hidden">  
                      <img  
                        src={movie.image}  
                        alt={movie.title}  
                        className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"  
                      />  
                      
                      <div className="absolute inset-0 bg-gradient-to-t from-gray-900/90 via-transparent to-transparent" />  
                      
                      {hasProgress && (
                        <div className="absolute bottom-0 left-0 right-0 p-2 bg-gradient-to-t from-black/80 to-transparent z-10">
                          <div className="flex items-center gap-2 mb-1">
                            <RotateCcw className="w-3 h-3 text-green-400" />
                            <span className="text-green-400 text-xs font-semibold">{progress}%</span>
                          </div>
                          <div className="w-full bg-gray-700/50 rounded-full h-1.5">
                            <div 
                              className="bg-green-500 h-1.5 rounded-full transition-all duration-500"
                              style={{ width: `${progress}%` }}
                            />
                          </div>
                        </div>
                      )}
                      
                      <div className="absolute top-3 right-3 flex gap-2">  
                        <motion.button  
                          onClick={(e) => { e.stopPropagation(); onToggleFavorite(movie.id); }}  
                          className="p-2 bg-black/60 hover:bg-red-500 rounded-lg backdrop-blur-sm transition-all duration-300"  
                          whileHover={{ scale: 1.1 }}  
                          whileTap={{ scale: 0.9 }}  
                        >  
                          <Heart  
                            className={`w-4 h-4 transition-all ${
                              isFavorite 
                                ? 'fill-red-500 text-red-500 scale-110' 
                                : 'text-white group-hover:scale-110'
                            }`}  
                          />  
                        </motion.button>  
                      </div>  

                      <motion.div  
                        className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300"  
                        initial={{ scale: 0.8 }}  
                        whileHover={{ scale: 1 }}  
                      >  
                        <motion.button  
                          onClick={(e) => { e.stopPropagation(); onPlayVideo(movie, progress); }}
                          className="bg-gradient-to-r from-red-500 to-pink-500 text-white p-3 md:p-4 rounded-full shadow-2xl flex items-center gap-2 group/play"  
                          whileHover={{ scale: 1.1 }}  
                          whileTap={{ scale: 0.9 }}  
                        >  
                          <Play className="w-4 h-4 md:w-5 md:h-5 group-hover/play:scale-110 transition-transform" />  
                          <span className="text-sm font-semibold">
                            {hasProgress ? 'Continuar' : 'Reproducir'}
                          </span>
                        </motion.button>  
                      </motion.div>  

                      <div className="absolute bottom-3 left-3 flex items-center gap-1 bg-black/60 backdrop-blur-sm px-2 py-1 rounded-full z-10">  
                        <Star className="w-3 h-3 fill-yellow-400 text-yellow-400" />  
                        <span className="text-white text-sm font-semibold">{movie.rating}</span>  
                      </div>  

                      {movie.duration && (  
                        <div className="absolute bottom-3 right-3 bg-black/60 backdrop-blur-sm px-2 py-1 rounded-full z-10">  
                          <span className="text-white text-xs font-medium">{movie.duration}</span>  
                        </div>  
                      )}  
                    </div>  

                    <div className="p-3 md:p-4 lg:p-6 flex-1">  
                      <h4 className="text-white font-bold text-base md:text-lg lg:text-xl mb-2 line-clamp-1 group-hover:text-red-400 transition-colors">  
                        {movie.title}  
                      </h4>  

                      <div className="flex items-center gap-2 text-gray-400 text-xs md:text-sm mb-2 md:mb-3">  
                        {movie.year && (  
                          <div className="flex items-center gap-1">  
                            <Calendar className="w-3 h-3" />  
                            <span>{movie.year}</span>  
                          </div>  
                        )}  
                        {movie.genres && movie.genres.length > 0 && (  
                          <>  
                            <span className="text-gray-600">•</span>  
                            <span className="line-clamp-1">{movie.genres.slice(0, 2).join(', ')}</span>  
                          </>  
                        )}  
                      </div>  

                      <div className="mb-3 md:mb-4">
                        <p className="text-gray-300 text-xs md:text-sm line-clamp-2 leading-relaxed">  
                          {movie.description || 'Una historia épica que no querrás perderte.'}  
                        </p>
                        <button
                          onClick={(e) => { e.stopPropagation(); openModal(movie); }}
                          className="mt-1 text-white text-xs font-semibold hover:text-gray-300 transition-colors flex items-center gap-1"
                        >
                          <span>Ver más</span>
                          <Info className="w-3 h-3" />
                        </button>
                      </div>

                      <div className="flex gap-2">  
                        <motion.button  
                          onClick={(e) => { e.stopPropagation(); onPlayVideo(movie, progress); }}
                          className="flex-1 bg-gradient-to-r from-red-500 to-pink-500 hover:from-red-600 hover:to-pink-600 text-white py-2 md:py-2.5 rounded-lg text-xs md:text-sm font-semibold transition-all duration-300 flex items-center justify-center gap-1 md:gap-2 group/play"  
                          whileHover={{ scale: 1.02, y: -1 }}  
                          whileTap={{ scale: 0.98 }}  
                        >  
                          <Play className="w-3 h-3 md:w-4 md:h-4 group-hover/play:scale-110 transition-transform" />  
                          <span>{hasProgress ? 'Continuar' : 'Reproducir'}</span>
                        </motion.button>  
                        
                        <motion.button  
                          onClick={(e) => { e.stopPropagation(); onToggleFavorite(movie.id); }}  
                          className={`px-2 md:px-3 rounded-lg transition-all duration-300 border flex items-center justify-center ${
                            isFavorite 
                              ? 'bg-red-500/20 border-red-500/50 text-red-400 hover:bg-red-500/30' 
                              : 'bg-gray-700/50 border-gray-600/50 text-gray-300 hover:bg-gray-600/50 hover:text-white hover:border-gray-500/50'
                          }`}  
                          whileHover={{ scale: 1.05 }}  
                          whileTap={{ scale: 0.95 }}  
                        >  
                          <Heart className={`w-3 h-3 md:w-4 md:h-4 ${isFavorite ? 'fill-current' : ''}`} />  
                        </motion.button>  
                      </div>

                      {hasProgress && (
                        <div className="flex items-center gap-2 text-green-400 text-xs mt-2">
                          <RotateCcw className="w-3 h-3" />
                          <span>{progress}% visto</span>
                        </div>
                      )}  
                    </div>  

                    <div className="absolute inset-0 rounded-xl md:rounded-2xl bg-gradient-to-r from-red-500/0 via-pink-500/0 to-purple-500/0 group-hover:from-red-500/10 group-hover:via-pink-500/5 group-hover:to-purple-500/10 transition-all duration-500 pointer-events-none" />  
                  </div>  
                </div>  
              );
            })}  
            </div>  
          </div>  

          {scrollX < getMaxScroll() && (  
            <motion.button  
              onClick={scrollRight}  
              className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-2 z-20 bg-black/80 hover:bg-black text-white p-3 rounded-full backdrop-blur-sm border border-gray-700/50 shadow-2xl hidden md:block"  
              whileHover={{ scale: 1.1, x: 2 }}  
              whileTap={{ scale: 0.9 }}  
              initial={{ opacity: 0, x: 10 }}  
              animate={{ opacity: 1, x: 0 }}  
            >  
              <ChevronRight className="w-5 h-5" />  
            </motion.button>  
          )}  
        </div>  

        <div className="flex justify-center items-center gap-3 mt-6 md:hidden">  
          <motion.button  
            onClick={scrollLeft}  
            className="p-2 bg-gray-800/50 hover:bg-gray-700/50 text-gray-400 hover:text-white rounded-lg transition-all duration-300 border border-gray-700/50"  
            whileHover={{ scale: 1.1, x: -2 }}  
            whileTap={{ scale: 0.9 }}  
          >  
            <ChevronLeft className="w-4 h-4" />  
          </motion.button>  
          
          <div className="flex gap-1">  
            {Array.from({ length: Math.ceil(movies.length / 3) }).map((_, index) => (  
              <div  
                key={index}  
                className={`w-1.5 h-1.5 rounded-full transition-all duration-300 ${
                  scrollX >= index * 400 && scrollX < (index + 1) * 400 
                    ? 'bg-red-500 w-3' 
                    : 'bg-gray-600'
                }`}  
              />  
            ))}  
          </div>  
          
          <motion.button  
            onClick={scrollRight}  
            className="p-2 bg-gray-800/50 hover:bg-gray-700/50 text-gray-400 hover:text-white rounded-lg transition-all duration-300 border border-gray-700/50"  
            whileHover={{ scale: 1.1, x: 2 }}  
            whileTap={{ scale: 0.9 }}  
          >  
            <ChevronRight className="w-4 h-4" />  
          </motion.button>  
        </div>  
      </div>

      {selectedMovie && (
        <MovieDetailModal
          movie={selectedMovie}
          isOpen={isModalOpen}
          onClose={closeModal}
          onPlayVideo={onPlayVideo}
          onToggleFavorite={onToggleFavorite}
          isFavorite={favorites.includes(selectedMovie.id)}
          progress={watchProgress[selectedMovie.id] || getWatchProgress(selectedMovie.id) || 0}
        />
      )}
    </motion.section>  
  );  
};  

export default MovieCarousel;